import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@noor/lib/cn';

/**
 * Typography primitives. Every size is sourced from the type-scale tokens
 * (text-2xs … text-5xl) and every color from the foreground / muted-foreground
 * roles — never a literal value. These are the lowest-level text components the
 * rest of the design system composes on top of.
 */

// ── Heading ──────────────────────────────────────────────────────────────────
// Six display levels mapped onto the type scale. `level` controls *both* the
// rendered element (h1…h6) and the default size; a caller can still override the
// size via `className` or render a different element with `asChild`.
const headingVariants = cva('font-semibold tracking-tight text-foreground text-balance', {
  variants: {
    level: {
      1: 'text-5xl',
      2: 'text-4xl',
      3: 'text-3xl',
      4: 'text-2xl',
      5: 'text-xl',
      6: 'text-base',
    },
  },
  defaultVariants: {
    level: 1,
  },
});

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

type HeadingProps = Omit<React.ComponentProps<'h1'>, 'color'> &
  VariantProps<typeof headingVariants> & {
    /** Heading level 1–6. Drives the rendered tag and the default size. */
    level?: HeadingLevel;
    /** Render the styles onto the single child element instead of an `h*` tag. */
    asChild?: boolean;
  };

function Heading({ className, level = 1, asChild = false, ...props }: HeadingProps) {
  const Comp = asChild ? Slot : (`h${level}` as const);
  return (
    <Comp
      data-slot="heading"
      className={cn(headingVariants({ level, className }))}
      {...props}
    />
  );
}

// ── Text ─────────────────────────────────────────────────────────────────────
// Inline/paragraph copy. Variants pick a size from the type scale and a color
// role; `body` is the default. Renders a `<p>` unless `asChild` is set.
const textVariants = cva('text-foreground', {
  variants: {
    variant: {
      body: 'text-base text-foreground',
      lead: 'text-lg text-muted-foreground',
      large: 'text-lg font-medium text-foreground',
      small: 'text-sm text-foreground',
      muted: 'text-sm text-muted-foreground',
      caption: 'text-2xs text-muted-foreground',
    },
  },
  defaultVariants: {
    variant: 'body',
  },
});

type TextProps = Omit<React.ComponentProps<'p'>, 'color'> &
  VariantProps<typeof textVariants> & {
    /** Render the styles onto the single child element instead of a `<p>`. */
    asChild?: boolean;
  };

function Text({ className, variant, asChild = false, ...props }: TextProps) {
  const Comp = asChild ? Slot : 'p';
  return (
    <Comp
      data-slot="text"
      className={cn(textVariants({ variant, className }))}
      {...props}
    />
  );
}

export { Heading, Text, headingVariants, textVariants };
export type { HeadingProps, HeadingLevel, TextProps };
